import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Popconfirm,
  message,
  DatePicker,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import productService from '../../services/productService';
import pricingGroupService from '../../services/pricingGroupService';
import specialProductPricingTiersService from '../../services/specialProductPricingTiersService';
import investorService from '../../services/investorService';
import ifaService from '../../services/ifaService';
import useRemoteSelectPage from '../../hooks/useRemoteSelectPage';

const SELECT_PAGE_SIZE = 50;

const getProductId = (product) => product?.id;
const getSubjectKey = (subject) => subject?.uuid ?? subject?.id;

function buildSubjectOption(subject) {
  const key = getSubjectKey(subject);
  return {
    value: key,
    label: [subject?.name, subject?.pan].filter(Boolean).join(' · ') || String(key),
  };
}

export default function SpecialProductPricingTiersPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_SPECIAL_PRODUCT_TIERS_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_SPECIAL_PRODUCT_TIERS_EDIT);

  const [groups, setGroups] = useState([]);
  const [productId, setProductId] = useState(undefined);
  const [pricingGroupId, setPricingGroupId] = useState(undefined);
  const [subjectScope, setSubjectScope] = useState('user');
  const [subjectUuid, setSubjectUuid] = useState('');
  const [subjectLabelCache, setSubjectLabelCache] = useState('');
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resolveQty, setResolveQty] = useState(1);
  const [resolving, setResolving] = useState(false);
  const [form] = Form.useForm();
  const isEdit = Boolean(editing);

  const maxOrder = tiers.length ? Math.max(...tiers.map((s) => Number(s.tier_order) || 0)) : 0;

  const subjectFieldLabel =
    subjectScope === 'user' ? 'Investor (search name or PAN)' : 'Partner / IFA (search name or PAN)';

  const fetchProductsPage = useCallback(async ({ page, limit, search, signal }) => {
    const response = await productService.getProducts({
      page,
      limit,
      search,
      sort_by: 'preference_index',
      sort_order: 'asc',
      signal,
    });
    const data = response?.data ?? response;
    const list = data?.products ?? data?.data ?? data?.items ?? [];
    return {
      items: Array.isArray(list) ? list : [],
      pagination: data?.pagination,
    };
  }, []);

  const {
    items: products,
    loading: productsLoading,
    handleSearch: handleProductSearch,
    handlePopupScroll: handleProductPopupScroll,
    handleOpenChange: handleProductOpenChange,
    dropdownRender: productDropdownRender,
    searchValue: productSearchValue,
    clearSearch: clearProductSearch,
  } = useRemoteSelectPage({
    fetchPage: fetchProductsPage,
    getKey: getProductId,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: productId,
    onError: (e) => message.error(e?.response?.data?.message || e?.message || 'Failed to load products'),
  });

  const fetchSubjectsPage = useCallback(
    async ({ page, limit, search, signal }) => {
      const params = {
        page,
        limit,
        search,
        status: 'all',
        signal,
      };
      const result =
        subjectScope === 'user'
          ? await investorService.getAdminInvestors(params)
          : await ifaService.getAdminIfas(params);
      const list = result?.users ?? result?.data ?? result?.items ?? [];
      return {
        items: Array.isArray(list) ? list : [],
        pagination: result?.pagination,
      };
    },
    [subjectScope]
  );

  const {
    items: subjects,
    loading: subjectsLoading,
    handleSearch: handleSubjectSearch,
    handlePopupScroll: handleSubjectPopupScroll,
    handleOpenChange: handleSubjectOpenChange,
    dropdownRender: subjectDropdownRender,
    searchValue: subjectSearchValue,
    clearSearch: clearSubjectSearch,
  } = useRemoteSelectPage({
    fetchPage: fetchSubjectsPage,
    getKey: getSubjectKey,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: subjectUuid,
    onError: () => message.error(`Failed to load ${subjectScope === 'user' ? 'investors' : 'partners'}`),
  });

  const handleProductChange = useCallback((value) => {
    setProductId(value);
  }, []);

  const handleProductClear = useCallback(() => {
    setProductId(undefined);
    clearProductSearch();
  }, [clearProductSearch]);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const gList = await pricingGroupService.list();
      setGroups(Array.isArray(gList) ? gList : []);
    } catch (e) {
      message.error(e?.message || 'Failed to load pricing groups');
      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    setSubjectUuid('');
    setSubjectLabelCache('');
    clearSubjectSearch();
  }, [subjectScope, clearSubjectSearch]);

  const canList = productId && pricingGroupId && subjectUuid && subjectUuid.trim().length > 0;

  const loadTiers = useCallback(async () => {
    if (!canList) {
      setTiers([]);
      return;
    }
    setLoading(true);
    try {
      const subject =
        subjectScope === 'user' ? { userId: subjectUuid.trim() } : { partnerId: subjectUuid.trim() };
      const { tiers: list } = await specialProductPricingTiersService.list(productId, pricingGroupId, subject);
      setTiers(Array.isArray(list) ? list : []);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load special product tiers');
      setTiers([]);
    } finally {
      setLoading(false);
    }
  }, [canList, productId, pricingGroupId, subjectScope, subjectUuid]);

  useEffect(() => {
    loadTiers();
  }, [loadTiers]);

  const onTryResolve = async () => {
    if (!canList) {
      message.warning('Select product, pricing group, and investor or partner first.');
      return;
    }
    setResolving(true);
    try {
      const params = {
        productId,
        pricingGroupId,
        quantity: resolveQty,
      };
      if (subjectScope === 'user') params.userId = subjectUuid.trim();
      else params.partnerId = subjectUuid.trim();

      const res = await specialProductPricingTiersService.resolve(params);
      if (res?.success === false) {
        message.error(res?.message || 'Resolve failed');
        return;
      }
      const payload = res?.data ?? {};
      const src = payload.source;
      const row = payload.row;
      message.info(
        `Source: ${src || '—'} | Unit price: ${row?.pricing_per_unit != null ? row.pricing_per_unit : '—'}`,
        5,
      );
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Resolve failed');
    } finally {
      setResolving(false);
    }
  };

  const openCreate = () => {
    if (!canList) {
      message.warning('Select product, pricing group, and investor or partner first.');
      return;
    }
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      tier_order: maxOrder + 1 || 1,
      product_id: productId,
      pricing_group_id: pricingGroupId,
    });
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      tier_order: record.tier_order,
      min_qty: record.min_qty,
      max_qty: record.max_qty,
      pricing_per_unit: record.pricing_per_unit,
      min_price: record.min_price,
      max_price: record.max_price,
      valid_from: record.valid_from ? dayjs(record.valid_from) : null,
      valid_until: record.valid_until ? dayjs(record.valid_until) : null,
      max_total_qty: record.max_total_qty,
    });
    setDrawerOpen(true);
  };

  const onClose = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const toIso = (d) => (d && dayjs.isDayjs(d) ? d.toISOString() : d || null);

      if (isEdit) {
        const res = await specialProductPricingTiersService.update(editing.id, {
          tier_order: values.tier_order,
          min_qty: values.min_qty,
          max_qty: values.max_qty === undefined || values.max_qty === '' ? null : values.max_qty,
          pricing_per_unit: values.pricing_per_unit,
          min_price: values.min_price,
          max_price: values.max_price === undefined || values.max_price === '' ? null : values.max_price,
          valid_from: values.valid_from ? toIso(values.valid_from) : null,
          valid_until: values.valid_until ? toIso(values.valid_until) : null,
          max_total_qty: values.max_total_qty === undefined || values.max_total_qty === '' ? null : values.max_total_qty,
        });
        if (res?.success === false) {
          message.error(res?.message || 'Update failed');
          return;
        }
        message.success('Special product tier updated');
      } else {
        const payload = {
          product_id: values.product_id,
          pricing_group_id: values.pricing_group_id,
          tier_order: values.tier_order,
          min_qty: values.min_qty,
          max_qty: values.max_qty === undefined || values.max_qty === '' ? null : values.max_qty,
          pricing_per_unit: values.pricing_per_unit,
          min_price: values.min_price,
          max_price: values.max_price === undefined || values.max_price === '' ? null : values.max_price,
          valid_from: values.valid_from ? toIso(values.valid_from) : null,
          valid_until: values.valid_until ? toIso(values.valid_until) : null,
          max_total_qty: values.max_total_qty === undefined || values.max_total_qty === '' ? null : values.max_total_qty,
        };
        if (subjectScope === 'user') {
          payload.user_id = subjectUuid.trim();
        } else {
          payload.partner_id = subjectUuid.trim();
        }

        const res = await specialProductPricingTiersService.create(payload);
        if (res?.success === false) {
          message.error(res?.message || 'Create failed');
          return;
        }
        message.success('Special product tier created');
        setProductId(values.product_id);
        setPricingGroupId(values.pricing_group_id);
      }
      onClose();
      loadTiers();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (record) => {
    try {
      const res = await specialProductPricingTiersService.remove(record.id);
      if (res?.success === false) {
        message.error(res?.message || 'Delete failed');
        return;
      }
      message.success('Special product tier deleted');
      loadTiers();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        label: p.name || p.slug || p.id,
      })),
    [products]
  );

  const groupOptions = useMemo(
    () =>
      groups.map((g) => ({
        value: g.id,
        label: g.group_name || g.id,
      })),
    [groups]
  );

  const subjectOptions = useMemo(
    () =>
      subjects
        .map(buildSubjectOption)
        .filter((option) => option.value != null && option.value !== ''),
    [subjects]
  );

  const subjectSelectOptions = useMemo(
    () =>
      !subjectUuid || subjectOptions.some((o) => o.value === subjectUuid)
        ? subjectOptions
        : [{ value: subjectUuid, label: subjectLabelCache || subjectUuid }, ...subjectOptions],
    [subjectLabelCache, subjectOptions, subjectUuid]
  );

  const formatQty = (v) => String(Math.round(Number(v)));

  const columns = [
    { title: 'Order', dataIndex: 'tier_order', key: 'tier_order', width: 70 },
    { title: 'Min qty', dataIndex: 'min_qty', key: 'min_qty', width: 80, render: (v) => formatQty(v) },
    {
      title: 'Max qty',
      dataIndex: 'max_qty',
      key: 'max_qty',
      width: 90,
      render: (v) => (v == null || v === '' ? '— (open)' : formatQty(v)),
    },
    { title: 'Price / unit', dataIndex: 'pricing_per_unit', key: 'pricing_per_unit', width: 100 },
    { title: 'Min price', dataIndex: 'min_price', key: 'min_price', width: 90 },
    {
      title: 'Max price',
      dataIndex: 'max_price',
      key: 'max_price',
      width: 90,
      render: (v) => (v == null || v === '' ? '— (open)' : v),
    },
    {
      title: 'Valid',
      key: 'valid',
      width: 200,
      render: (_, r) => {
        const a = r.valid_from ? dayjs(r.valid_from).format('DD MMM YY HH:mm') : '—';
        const b = r.valid_until ? dayjs(r.valid_until).format('DD MMM YY HH:mm') : '—';
        return `${a} → ${b}`;
      },
    },
    {
      title: 'Max total qty',
      dataIndex: 'max_total_qty',
      key: 'max_total_qty',
      width: 100,
      render: (v) => (v == null || v === '' ? '—' : formatQty(v)),
    },
    {
      title: 'Actions',
      key: 'act',
      width: 200,
      fixed: 'right',
      render: (_, r) => (
        <Space>
          {canEdit && (
            <Button type="default" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
              Edit
            </Button>
          )}
          {canEdit && (
            <Popconfirm
              title="Delete this special product tier?"
              onConfirm={() => onDelete(r)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Special product pricing tiers</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Per-investor or per-partner: when rows exist for a subject, they override default{' '}
          <code className="text-xs">product_pricing_tiers</code> for the same product + pricing group. Search investors or
          partners by name or PAN. Optional validity window and max total quantity (cap in orders can be wired
          separately).
        </p>
      </div>

      <div className="flex flex-wrap gap-4 items-end mb-4 bg-white p-4 rounded-2xl border border-gray-100">
        <div className="min-w-[180px] flex-1">
          <div className="text-xs text-gray-500 mb-1">Product</div>
          <Select
            showSearch
            autoClearSearchValue={false}
            placeholder="Select product"
            optionFilterProp="label"
            filterOption={false}
            className="w-full"
            loading={productsLoading}
            value={productId}
            onChange={handleProductChange}
            onClear={handleProductClear}
            searchValue={productSearchValue}
            onSearch={handleProductSearch}
            onPopupScroll={handleProductPopupScroll}
            onOpenChange={handleProductOpenChange}
            dropdownRender={productDropdownRender}
            options={productOptions}
            allowClear
          />
        </div>
        <div className="min-w-[180px] flex-1">
          <div className="text-xs text-gray-500 mb-1">Pricing group</div>
          <Select
            showSearch
            placeholder="Select group"
            optionFilterProp="label"
            className="w-full"
            loading={groupsLoading}
            value={pricingGroupId}
            onChange={setPricingGroupId}
            options={groupOptions}
            allowClear
          />
        </div>
        <div className="min-w-[120px]">
          <div className="text-xs text-gray-500 mb-1">Subject</div>
          <Select
            className="w-full"
            value={subjectScope}
            onChange={setSubjectScope}
            options={[
              { value: 'user', label: 'Investor' },
              { value: 'partner', label: 'Partner' },
            ]}
          />
        </div>
        <div className="min-w-[260px] flex-1">
          <div className="text-xs text-gray-500 mb-1">{subjectFieldLabel}</div>
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            optionFilterProp="label"
            filterOption={false}
            className="w-full"
            placeholder="Select investor or partner (search name or PAN)"
            loading={subjectsLoading}
            notFoundContent={subjectsLoading ? 'Loading…' : 'No investors or partners found.'}
            value={subjectUuid || undefined}
            options={subjectSelectOptions}
            searchValue={subjectSearchValue}
            onSearch={handleSubjectSearch}
            onPopupScroll={handleSubjectPopupScroll}
            onOpenChange={handleSubjectOpenChange}
            dropdownRender={subjectDropdownRender}
            onChange={(value) => {
              if (!value) {
                setSubjectUuid('');
                setSubjectLabelCache('');
                return;
              }
              setSubjectUuid(value);
              const opt = subjectOptions.find((o) => o.value === value);
              setSubjectLabelCache(opt?.label || value);
            }}
            onClear={() => {
              setSubjectUuid('');
              setSubjectLabelCache('');
              clearSubjectSearch();
            }}
          />
        </div>
        {canList && (
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <div className="text-xs text-gray-500 mb-1">Test qty (resolve)</div>
              <InputNumber min={1} precision={0} value={resolveQty} onChange={(v) => setResolveQty(v || 1)} />
            </div>
            <Button icon={<SearchOutlined />} loading={resolving} onClick={onTryResolve}>
              Which tier?
            </Button>
          </div>
        )}
        {canCreate && canList && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} className="bg-emerald-600! border-emerald-600!">
            Add special product tier
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-0 overflow-hidden">
        <Table
          rowKey={(r) => r.id}
          columns={columns}
          dataSource={tiers}
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
          locale={{
            emptyText: canList
              ? 'No special product tiers. Add a row to override default group tiers for this subject.'
              : 'Select product, pricing group, and investor or partner (search by name or PAN).',
          }}
        />
      </div>

      <Drawer
        title={isEdit ? 'Edit special product tier' : 'New special product tier'}
        width={440}
        open={drawerOpen}
        onClose={onClose}
        extra={
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={onSubmit} className="bg-emerald-600! border-emerald-600!">
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          {!isEdit && (
            <>
              <Form.Item name="product_id" label="Product" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch
                  autoClearSearchValue={false}
                  options={productOptions}
                  optionFilterProp="label"
                  filterOption={false}
                  placeholder="Product"
                  loading={productsLoading}
                  searchValue={productSearchValue}
                  onSearch={handleProductSearch}
                  onClear={clearProductSearch}
                  onPopupScroll={handleProductPopupScroll}
                  onOpenChange={handleProductOpenChange}
                  dropdownRender={productDropdownRender}
                />
              </Form.Item>
              <Form.Item
                name="pricing_group_id"
                label="Pricing group"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select showSearch options={groupOptions} optionFilterProp="label" placeholder="Group" />
              </Form.Item>
            </>
          )}
          <Form.Item name="tier_order" label="Tier order" rules={[{ required: true, message: 'Required' }]}>
            <Input type="number" min={1} step={1} />
          </Form.Item>
          <Form.Item name="min_qty" label="Min quantity" rules={[{ required: true, message: 'Required' }]}>
            <InputNumber className="w-full" min={1} precision={0} step={1} />
          </Form.Item>
          <Form.Item name="max_qty" label="Max quantity (empty = open)">
            <InputNumber className="w-full" min={1} precision={0} step={1} allowClear />
          </Form.Item>
          <Form.Item name="pricing_per_unit" label="Price per unit" rules={[{ required: true, message: 'Required' }]}>
            <InputNumber className="w-full" min={0} precision={2} step={0.01} />
          </Form.Item>
          <Form.Item name="min_price" label="Min price" rules={[{ required: true, message: 'Required' }]}>
            <InputNumber className="w-full" min={0} precision={2} step={0.01} />
          </Form.Item>
          <Form.Item name="max_price" label="Max price (empty = open)">
            <InputNumber className="w-full" min={0} precision={2} step={0.01} />
          </Form.Item>
          <Form.Item name="valid_from" label="Valid from (optional)">
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item name="valid_until" label="Valid until (optional)">
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item name="max_total_qty" label="Max total units under this special (optional)">
            <InputNumber className="w-full" min={1} precision={0} step={1} allowClear />
          </Form.Item>
        </Form>
      </Drawer>
    </DashboardPageWrapper>
  );
}
